// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file <INSERT_FILE_DESCRIPTION_HERE>
 * @author James Reid
 */

// @ts-check

// @@imports-package
import { Membrane } from "../products/index.js"
import { Stock, Bom } from "../stock/index.js"

// @@imports-module
import { TapeCalculator } from "./Tape.js"
import { AdhesiveCalculator } from "./Adhesive.js"

// @@imports-utils
import { variance, DoubleLinkedList, Rectangle, DoubleLinkedListNode, Vector2D, imperialToMetric } from "../utils/index.js"

// @@imports-types
import {
    Config,
    TAPE_TYPES,
    JOIN_TYPES,
    MEMBRANE_TYPES,
    ADHESIVE_TYPES,
    // MembraneOption
} from "../types/index.js"
import { MembraneOption } from "../types/MembraneOption.js"

// @@body
/**
 * @todo Complete method documentation including inline comments, jsdoc
 * annotations, and class description.
 * @todo Check that all error throws use decorated error format.
 * @todo Note in class desc that deck adhesives for entire roof are handled
 * elsewhere.
 * @todo Add additional membrane option class and/or reduce amount of properties
 * in membrane option.
 * @todo Force fleece backed to use tapeless seam joins instead, and default to
 * butt joins everywhere else.
 */
class MembraneCalculator {
    #membraneId = 1
    #stock
    #config
    #tapes
    #adhesives
    #bom = new Bom()

    /**
     * 
     * @param {Stock} stock 
     * @param {Config} config
     * @param {AdhesiveCalculator} adhesiveCalculator
     * @param {TapeCalculator} tapeCalculator
     */
    constructor(stock, config, adhesiveCalculator, tapeCalculator) {
        this.#stock = stock
        this.#config = config
        this.#tapes = tapeCalculator
        this.#adhesives = adhesiveCalculator
    }

    /**
     * 
     * @param {number} width 
     * @param {number} depth 
     * @param {Rectangle} zone
     * @param {object} obj
     * @param {number} [obj.membraneCount]
     * @param {boolean} [obj.flipped]
     * @param {boolean} [obj.canFlip]
     * @param {boolean} [obj.forceFlip]
     * @returns {void}
     */
    addDeckZone(width, depth, zone, {
        membraneCount = 1,
        flipped = false,
        canFlip = true,
        forceFlip = false
    } = {}) {
        if (forceFlip) {
            const options = { canFlip: false, flipped: true }
            return this.addDeckZone(depth, width, zone, options)
        }

        const joinTapeType = this.#config.rubberJoin === JOIN_TYPES.butt ||
            this.#config.membrane.type === MEMBRANE_TYPES.fleeceBacked
                ? TAPE_TYPES.cured
                : TAPE_TYPES.seam

        const { membrane: { rollLength } } = this.#config
        if (depth > rollLength) {
            const overhang = joinTapeType === TAPE_TYPES.cured
                ? this.#config.overhangs.buttJoin.client
                : this.#config.overhangs.seamJoin.client
            const { anchor } = zone

            while (depth > 0) {
                const height = (depth > rollLength ? rollLength : depth) * 100
                const zone = flipped
                    ? new Rectangle(anchor, height, width * 100)
                    : new Rectangle(anchor, width * 100, height)
                const options = { canFlip, flipped }

                if (depth > rollLength) {
                    this.addDeckZone(width, rollLength, zone, options)
                    this.#tapes.addTape(width, joinTapeType)
                }
                else {
                    this.addDeckZone(width, depth, zone, options)
                }

                flipped
                    ? anchor.set({ x: anchor.x + rollLength * 100 })
                    : anchor.set({ y: anchor.y + rollLength * 100 })
                depth -= rollLength - overhang
            }

            return
        }
        else if (width > rollLength) { canFlip = false }

        const membraneOption = this.#getMembraneOptions(width, depth, {
            membraneCount,
            flipped,
            canFlip
        }).at(0)
        if (!membraneOption) {
            throw new Error("Insufficient membrane coverage for roof")
        }

        for (const membrane of membraneOption.membranes) {
            const detail = {
                id: this.#membraneId++,
                zone: zone.serialize(),
                fraction: membrane.width / membraneOption.width.membrane,
                flipped: membraneOption.flipped
            }
            this.#bom.addLineItem(membrane, membraneOption.depth, { detail })
            membrane.runningStock -= membraneOption.depth
        }

        if (membraneOption.seams.length > 0) {
            // Calculate effective width and length of seam in the event of
            // needing primer for a pre-applied length of seam tape as found on
            // fleece-backed membrane.
            const width = imperialToMetric({ inches: 3 })
            const length = membraneOption.seams.length
            const { primerBorder } = this.#config.tapes

            this.#config.membrane.type === MEMBRANE_TYPES.fleeceBacked
                ? this.#adhesives.addAdhesive(
                    ADHESIVE_TYPES.primer,
                    (width + primerBorder) * (length + primerBorder)
                )
                : this.#tapes.addTape(length, joinTapeType)
        }
    }

    /**
     * 
     * @param {number} deckWidth 
     * @param {number} deckDepth 
     * @param {object} obj
     * @param {number} [obj.membraneCount]
     * @param {boolean} [obj.flipped]
     * @param {boolean} [obj.canFlip]
     * @param {boolean} [obj.forceFlip]
     * @param {boolean} [obj.isRoot]
     * @returns {MembraneOption[]}
     */
    #getMembraneOptions(deckWidth, deckDepth, {
        membraneCount = 1,
        flipped = false,
        canFlip = true,
        forceFlip = false,
        isRoot = true
    } = {}) {
        // Flips irrespective of value of can flip.
        // if (forceFlip) {
        //     return this.#getMembraneOptions(deckDepth, deckWidth, {
        //         membraneCount,
        //         flipped: true,
        //         canFlip: false,
        //         forceFlip: false,
        //         isRoot: true
        //     })
        // }

        let options = []

        //
        if (canFlip && !flipped && deckWidth !== deckDepth) {
            options.push(...this.#getMembraneOptions(deckDepth, deckWidth, {
                membraneCount,
                flipped: true,
                canFlip: false,
                // forceFlip: false,
                isRoot: false
            }))
        }

        //
        const membraneWidth = this.#convertWidth(
            deckWidth,
            membraneCount,
            "deckToMembrane"
        )
        const membraneDepth = Math.ceil(deckDepth * 10) / 10

        //
        const membranes = this
            .#getMembraneLinkedList(membraneWidth, membraneDepth)
        if (!membranes.head) { return [] }
        if (membranes.head.value.width * membraneCount < membraneWidth) {
            options.push(...this.#getMembraneOptions(deckWidth, deckDepth, {
                membraneCount: ++membraneCount,
                flipped,
                canFlip: false,
                // forceFlip: false,
                isRoot: false
            }))
        }
        else {
            options = this.#getReducedMembraneOptions(
                membranes.head,
                membraneWidth,
                membraneDepth,
                membraneCount,
                flipped
            )
            options = this.#filterMembraneOptions(options)
            if (!options.length) {
                options.push(...this.#getMembraneOptions(deckWidth, deckDepth, {
                    membraneCount: ++membraneCount,
                    flipped,
                    canFlip: false,
                    // forceFlip: false,
                    isRoot: false
                }))
            }
        }

        return isRoot
            ? this.#sortMembraneOptions(options)
            : options
    }

    /**
     * 
     * @param {DoubleLinkedListNode.<Membrane>} headNode 
     * @param {number} width
     * @param {number} depth
     * @param {number} membraneCount
     * @param {boolean} flipped
     * @returns {MembraneOption[]}
     */
    #getReducedMembraneOptions(headNode, width, depth, membraneCount, flipped) {
        //
        const option = this.#getMembraneOption(
            Array.from({ length: membraneCount }, () => headNode),
            width,
            depth,
            flipped
        )
        const optionIds = /** @type {Set.<string>} */ (new Set())
        const options = [option]
        let optionPointer = 0

        LOOP: while (optionPointer < options.length) {
            //
            const option = options.at(optionPointer++)
            if (!option) { continue }

            //
            for (const [index, membraneNode] of option.nodes.entries()) {
                //
                const nextNode = membraneNode.next
                if (!nextNode) { continue }

                //
                const nextOptionNodes = [...option.nodes]
                nextOptionNodes.splice(index, 1, nextNode)
                const nextOption = this
                    .#getMembraneOption(nextOptionNodes, width, depth, flipped)

                //
                const { width: { membrane: membraneWidth } } = nextOption
                if (membraneWidth >= width && !optionIds.has(nextOption.id)) {
                    options.splice(optionPointer, 0, nextOption)
                    optionIds.add(nextOption.id)

                    // if (optionPointer > 10 && nextOption.wasteScore < 1) { break LOOP }
                }
            }
        }

        return options
    }

    /**
     * 
     * @param {Array.<DoubleLinkedListNode.<Membrane>>} option
     * @param {number} width
     * @param {number} depth
     * @param {boolean} flipped
     * @returns {MembraneOption}
     */
    #getMembraneOption(option, width, depth, flipped) {
        // 
        const id = option
            .map(membrane => membrane.value.width)
            .sort((widthA, widthB) => widthB - widthA)
            .toString()

        // 
        const membraneWidth = option.reduce((acc, cur) => {
            return acc + cur.value.width
        }, 0)
        const deckWidth = this.#convertWidth(
            membraneWidth,
            option.length,
            "membraneToDeck"
        )
        const waste = membraneWidth - width

        // 
        const {
            upper: upperLimit,
            lower: lowerLimit
        } = this.#config.clientLimits.roll.waste
        const wasteScore = waste < lowerLimit ? 0
            : waste > upperLimit ? 1
            : (waste - lowerLimit) / (upperLimit - lowerLimit)

        // account for cross seams required at roll length max... 
        // 
        const membranes = option.map(membraneNode => membraneNode.value)
        const seams = {
            count: membranes.length - 1,
            length: depth * (membranes.length - 1)
        }
        const widths = membranes.map(membrane => membrane.width)
        const bom = new Bom()
        for (const membrane of membranes) { bom.addLineItem(membrane, depth) }

        // 
        const optionVariance = variance(widths)
        const stockVariance = variance(
            this.#stock.getMembranes().map(membrane => {
                return membranes.includes(membrane)
                    ? membrane.stock - depth
                    : membrane.stock
            })
        )

        return {
            id,
            flipped,
            width: { membrane: membraneWidth, deck: deckWidth, waste },
            depth,
            variance: { option: optionVariance, stock: stockVariance },
            wasteScore,
            nodes: option,
            membranes,
            seams,
            widths,
            bom
        }
    }

    /**
     * Generate linked list of useable membranes in *descending* order of roll
     * width for a given zone based on the following constraints:
     * - Available coverage of all returned membranes must *exceed* the total
     * required coverage of the zone.
     * - Membranes must not exceed client limits of roll width or total roll
     * weight at cut length.
     * @param {number} width Zone width to cover including extra width due to
     * joins between membranes across membrane roll width.
     * @param {number} depth Zone depth to cover by membrane cut length.
     * @param {object} obj Optional destructured parameters object.
     * @param {number} [obj.coverageReduction] Coverage reduction factor to
     * apply to available membranes as a safety margin.
     * @returns {DoubleLinkedList.<Membrane>} Linked list of valid membranes.
     */
    #getMembraneLinkedList(width, depth, { coverageReduction = 0.95 } = {}) {
        // Generate linked list of useable membranes in *descending* order of
        // roll width, with membranes not matching client limits filtered out,
        // tracking total available coverage from all stock.
        let coverage = 0
        const membranes = new DoubleLinkedList(this.#stock
            .getMembranes()
            .filter(({ runningStock, width, weight = 0 }) => {
                coverage += width * runningStock * coverageReduction
                return runningStock >= depth &&
                    width <= this.#config.clientLimits.roll.width &&
                    weight * depth <= this.#config.clientLimits.roll.weight
            })
            .sort((membraneA, membraneB) => membraneB.width - membraneA.width))

        return coverage > width * depth ? membranes : new DoubleLinkedList([])
    }

    /**
     * 
     * @param {MembraneOption[]} options 
     * @returns {MembraneOption[]}
     */
    #filterMembraneOptions(options) {
        // need to collapse bom first in each membrane option... 
        return options.filter(option => {
            for (const membrane of option.membranes) {
                if (membrane.runningStock < option.depth) { return false }
            }

            return true
        })
    }

    /**
     * 
     * @param {MembraneOption[]} options 
     * @returns {MembraneOption[]}
     */
    #sortMembraneOptions(options) {
        const stockVars = [...options].sort((optionA, optionB) => {
            return optionA.variance.stock - optionB.variance.stock
        })
        const optionVars = [...options].sort((optionA, optionB) => {
            return optionA.variance.option - optionB.variance.option
        })

        return options.sort((optionA, optionB) => {
            return optionA.membranes.length - optionB.membranes.length ||
                optionA.width.waste - optionB.width.waste ||
                optionA.bom.price - optionB.bom.price
                // stockVars.indexOf(optionA) - stockVars.indexOf(optionB) ||
                // optionVars.indexOf(optionA) - optionVars.indexOf(optionB) ||
                // (optionA.flipped ? 1 : 0) - (optionB.flipped ? 1 : 0)
        })
    }

    /**
     * Convert a rubber width between membrane width and effective deck width.
     * The membrane width is the total width of all of the membranes, whereas
     * the deck width is the effective deck coverage that the membrane can cover
     * after subtracting the membrane overlaps.
     * @param {number} width Width to convert.
     * @param {number} membraneCount Number of membranes across width, from
     * which number of joins required can be inferred.
     * @param {"deckToMembrane"|"membraneToDeck"} direction Conversion direction
     * to membrane from deck, or to deck from membrane.
     * @returns {number} Converted width value.
     */
    #convertWidth(width, membraneCount, direction) {
        const overlap = this.#config.membrane.type === MEMBRANE_TYPES.onePiece
            ? this.#config.overhangs[this.#config.rubberJoin].client
            : this.#config.overhangs.fleeceBackedJoin.client
        return direction === "deckToMembrane"
            ? width + overlap * (membraneCount - 1)
            : width - overlap * (membraneCount - 1)
    }

    get bom() {
        return this.#bom
    }
}

// @@exports
export { MembraneCalculator }
